
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScoreBreakdown } from '@/types/survey';
import { generatePersonalizedRecommendations } from '@/services/aiAnalyticsService';
import { CheckCircle, LightbulbIcon, AlertCircle, Clock, Award, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface RecommendationsViewProps {
  scoreBreakdown: ScoreBreakdown | null;
  industry: string;
}

const RecommendationsView: React.FC<RecommendationsViewProps> = ({ 
  scoreBreakdown, 
  industry 
}) => {
  // Generate recommendations based on score breakdown and industry
  const recommendations = React.useMemo(() => {
    if (!scoreBreakdown) return [];
    
    // Extract existing recommendations or generate new ones
    if (scoreBreakdown.recommendations && scoreBreakdown.recommendations.length > 0) {
      return scoreBreakdown.recommendations;
    } else {
      return generatePersonalizedRecommendations(scoreBreakdown, industry);
    }
  }, [scoreBreakdown, industry]);

  if (!scoreBreakdown) {
    return <div className="text-center py-6">No data available for recommendations</div>;
  }

  // Helper function to get recommendation priority level
  const getRecommendationPriority = (index: number, recommendation: string): { level: string; color: string; icon: JSX.Element } => {
    if (recommendation.startsWith('CRITICAL') || recommendation.startsWith('URGENT') || recommendation.startsWith('PRIORITY')) {
      return { 
        level: 'Critical', 
        color: 'bg-red-100 text-red-700 border-red-200',
        icon: <AlertCircle className="h-5 w-5 text-red-600" />
      };
    }
    
    if (index === 0 || index === 1) {
      return { 
        level: 'High', 
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: <Clock className="h-5 w-5 text-amber-600" />
      };
    }
    
    return { 
      level: 'Medium', 
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: <Award className="h-5 w-5 text-green-600" />
    };
  };

  // Helper function to get improvement estimate text
  const getImprovementEstimate = (recommendation: string): { area: string; points: string } => {
    // Extract improvement points from recommendation text (format: "+2 EA" or similar)
    const match = recommendation.match(/\+(\d+)\s+([A-Z]{2,3})/);
    if (match) {
      const points = match[1];
      const area = match[2];
      
      const areaMap: Record<string, string> = {
        'EA': 'Emotional Attachment',
        'ELV': 'Ethical and Legacy Value',
        'L': 'Leadership Commitment',
        'TB': 'Trust Building',
        'BC': 'Business Continuity',
        // Legacy mappings
        'FC': 'Leadership Commitment',
        'LQ': 'Ethical and Legacy Value'
      };
      
      return {
        area: areaMap[area] || area,
        points: `+${points} points`
      };
    }
    
    return {
      area: 'Overall Score',
      points: '+1-2 points'
    };
  };

  console.log("Recommendations data:", recommendations);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Enhanced Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex items-start gap-3">
              <LightbulbIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="mb-1 font-medium">Personalized for {industry} Industry</p>
                <p>These AI-powered recommendations are tailored to your specific scores and industry context. Recommendations are prioritized based on potential impact.</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            {recommendations && recommendations.length > 0 ? (
              recommendations.map((recommendation, index) => {
                const priority = getRecommendationPriority(index, recommendation);
                const improvement = getImprovementEstimate(recommendation);
                
                // Clean up recommendation text by removing the improvement indicator
                const cleanRecommendation = recommendation
                  .replace(/\+\d+\s+[A-Z]{2,3}/g, '')
                  .replace(/CRITICAL: /g, '')
                  .replace(/URGENT: /g, '')
                  .replace(/PRIORITY: /g, '')
                  .trim();
                  
                return (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="mt-0.5 flex-shrink-0">
                      {priority.icon}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={`${priority.color}`}>
                          {priority.level} Priority
                        </Badge>
                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                          {improvement.area}
                        </Badge>
                        <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                          Est. {improvement.points}
                        </Badge>
                      </div>
                      
                      <p className="text-gray-800 font-medium">{cleanRecommendation}</p>
                      
                      <div className="pt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {index === 0 ? 'Short-term' : index === 1 ? 'Mid-term' : 'Long-term'} implementation
                        </span>
                        <span>•</span>
                        <span className="flex items-center">
                          <ArrowUpRight className="h-3 w-3 mr-1" />
                          {priority.level === 'Critical' ? 'Immediate' : priority.level === 'High' ? 'High' : 'Moderate'} impact
                        </span>
                      </div>
                      
                      {index === 0 && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded text-sm text-amber-800">
                          <strong>Implementation tip:</strong> Start with small, measurable steps to build momentum quickly.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <div className="text-center">
                  <LightbulbIcon className="h-8 w-8 text-amber-400 mx-auto mb-3" />
                  <p>No specific recommendations available at this time</p>
                </div>
              </div>
            )}
          </div>
          
          {recommendations && recommendations.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                Implementation Strategy
              </h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                <li>Begin with your highest priority recommendation for maximum impact</li>
                <li>Create specific, measurable goals for each recommendation</li>
                <li>Establish a timeline with clear milestones for implementation</li>
                <li>Reassess your BEAM score after implementing key recommendations</li>
              </ol>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RecommendationsView;
